import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, concatLatestFrom } from '@ngrx/effects';
import { switchMap, catchError, map, of, Observable } from 'rxjs';
import { IdentityService } from 'src/app/core/identity.service';
import { InAppNotificationsService } from 'src/app/in-app-notifications/in-app-notifications.service';
import { InAppNotificationSentAlert } from 'src/app/in-app-notifications/models/in-app-notification-alert-data.model';
import * as InAppNotificationsActions from 'src/app/in-app-notifications/store/actions/in-app-notifications.actions';
import * as InAppNotificationPreferencesActions from 'src/app/in-app-notifications/store/actions/in-app-notification-preferences.actions';
import * as TicketsActions from 'src/app/root-store/ticket-store/actions/tickets.actions';
import { HttpResponseHelpers } from 'src/app/shared/helpers';
import { HubConnectionService } from 'src/app/shared/services/realtime-connection.service';
import { InAppNotificationMessage } from 'src/app/shared/services/realtime-constants';
import { ApplicationEnum } from 'src/app/shared/enums/application.enum';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { Store } from '@ngrx/store';
import { InAppNotificationsState } from 'src/app/in-app-notifications/store/in-app-notifications.state';
import {
    selectFilter,
    selectInAppNotifications
} from 'src/app/in-app-notifications/store/selectors/in-app-notifications.selectors';
import { KeyValuePair } from 'src/app/shared/models/key-value-pair.model';
import { InAppNotificationType } from 'src/app/in-app-notifications/models/in-app-notification-type.enum';

@Injectable()
export class InAppNotificationsEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly _service: InAppNotificationsService,
        private readonly _hubConnectionService: HubConnectionService,
        private readonly _identityService: IdentityService,
        private readonly _store: Store<InAppNotificationsState>
    ) {}

    getInAppNotifications$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                InAppNotificationsActions.getInAppNotifications,
                InAppNotificationsActions.markAllAsReadSuccess,
                InAppNotificationsActions.updateInAppNotificationSettingsSuccess
            ),
            concatLatestFrom(() => this._store.select(selectFilter)),
            switchMap(([, currentFilter]) => {
                return this._service.getInAppNotifications(currentFilter).pipe(
                    map((result) =>
                        InAppNotificationsActions.getInAppNotificationsSuccess({
                            inAppNotifications: result
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            InAppNotificationsActions.getInAppNotificationsFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    getUnreadInAppNotificationsCount$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                InAppNotificationsActions.getUnreadInAppNotificationsCount,
                InAppNotificationsActions.markAllAsReadSuccess,
                InAppNotificationsActions.updateInAppNotificationSettingsSuccess,
                InAppNotificationsActions.inAppNotificationSentAlertSuccess,
                TicketsActions.updateTaskNoteSuccess
            ),
            switchMap(() => {
                return this._service.getUnreadInAppNotificationsCount().pipe(
                    map((result) =>
                        InAppNotificationsActions.getUnreadInAppNotificationsCountSuccess({
                            unreadInAppNotificationCounts: result
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            InAppNotificationsActions.getUnreadInAppNotificationsCountFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    getUnreadInAppNotificationsCountForDelegation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                InAppNotificationsActions.getUnreadInAppNotificationsCountForDelegation,
                InAppNotificationsActions.markAllAsReadSuccess,
                InAppNotificationsActions.updateInAppNotificationSettingsSuccess,
                InAppNotificationsActions.inAppNotificationSentAlertSuccess
            ),
            switchMap((action) => {
                return this._service.getUnreadInAppNotificationsCountForDelegation(action.userId).pipe(
                    map((result) =>
                        InAppNotificationsActions.getUnreadInAppNotificationsCountForDelegationSuccess({
                            unreadInAppNotificationCounts: result
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            InAppNotificationsActions.getUnreadInAppNotificationsCountForDelegationFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    getUnreadInAppNotificationsTabCounters$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                InAppNotificationsActions.getUnreadInAppNotificationsTabCounters,
                InAppNotificationsActions.markAllAsReadSuccess,
                InAppNotificationsActions.updateInAppNotificationSettingsSuccess,
                InAppNotificationsActions.inAppNotificationSentAlertSuccess
            ),
            switchMap((action) => {
                let result: Observable<Array<KeyValuePair<InAppNotificationType, number>>>;

                if (action.userId) {
                    result = this._service.getUnreadInAppNotificationsTabCountersForDelegation(action.userId);
                } else {
                    result = this._service.getUnreadInAppNotificationsTabCounters();
                }
                return result.pipe(
                    map((result) =>
                        InAppNotificationsActions.getUnreadInAppNotificationsTabCountersSuccess({
                            unreadInAppNotificationsTabCounters: result
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            InAppNotificationsActions.getUnreadInAppNotificationsTabCountersFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    hasOverdueTasksCreatedByMe$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                InAppNotificationsActions.hasOverdueTasksCreatedByMe,
                InAppNotificationsActions.inAppNotificationSentAlertSuccess,
                InAppNotificationPreferencesActions.updateInAppNotificationPreferenceSuccess
            ),
            switchMap(() => {
                return this._service.hasOverdueTasksCreatedByMe().pipe(
                    map((result) =>
                        InAppNotificationsActions.hasOverdueTasksCreatedByMeSuccess({
                            hasOverdueTasksCreatedByMe: result
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            InAppNotificationsActions.hasOverdueTasksCreatedByMeFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    getInAppNotificationById$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(InAppNotificationsActions.getInAppNotificationById),
            switchMap((action) => {
                return this._service.getInAppNotificationById(action.notificationId).pipe(
                    map((result) =>
                        InAppNotificationsActions.getInAppNotificationByIdSuccess({
                            inAppNotification: result
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            InAppNotificationsActions.getInAppNotificationByIdFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    updateInAppNotificationSettings$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(InAppNotificationsActions.updateInAppNotificationSettings),
            switchMap((action) => {
                return this._service
                    .updateInAppNotificationSettings(action.notificationId, action.userId, action.settings)
                    .pipe(
                        map(() =>
                            InAppNotificationsActions.updateInAppNotificationSettingsSuccess({
                                message: `Notification has been marked as ${action.settings.isRead ? 'read' : 'unread'}`,
                                userId: action.userId
                            })
                        ),
                        catchError((error: unknown) =>
                            of(
                                InAppNotificationsActions.updateInAppNotificationSettingsFailure({
                                    error: HttpResponseHelpers.parseErrorResponse(error)
                                })
                            )
                        )
                    );
            })
        );
    });

    markAllAsRead$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(InAppNotificationsActions.markAllAsRead),
            switchMap((action) => {
                return this._service.markAllAsRead(action.parentInAppNotificationTypeId, action.userId).pipe(
                    map(() =>
                        InAppNotificationsActions.markAllAsReadSuccess({
                            message: 'All Notifications marked as read',
                            userId: action.userId
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            InAppNotificationsActions.markAllAsReadFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    inAppNotificationSentAlert$ = createEffect(() => {
        return this._hubConnectionService.coreHub
            .messagesOf<InAppNotificationSentAlert>(InAppNotificationMessage.SENT)
            .pipe(
                concatLatestFrom(() => this._store.select(selectInAppNotifications)),
                map(([data, inAppNotifications]) => {
                    const identity = this._identityService.current;
                    const aspNetUserId = identity.userId;
                    if (
                        data &&
                        data.applicationId === ApplicationEnum.NovaWeb &&
                        data.aspNetUserIds.includes(aspNetUserId) &&
                        inAppNotifications.findIndex((x) => x.id === data.notificationId) < 0
                    ) {
                        return InAppNotificationsActions.inAppNotificationSentAlertSuccess({ data, userId: '' });
                    } else {
                        return InAppNotificationsActions.noopAction();
                    }
                }),
                catchError((error: unknown) =>
                    of(
                        InAppNotificationsActions.inAppNotificationSentAlertFailure({
                            error: HttpResponseHelpers.parseErrorResponse(error)
                        })
                    )
                )
            );
    });

    getInAppNotificationsUser$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(InAppNotificationsActions.getInAppNotificationsUser),
            switchMap((action) => {
                return this._service.getInAppNotificationsUser(action.userId).pipe(
                    map((result) =>
                        InAppNotificationsActions.getInAppNotificationsUserSuccess({
                            user: result
                        })
                    ),
                    catchError((error: unknown) =>
                        of(
                            InAppNotificationsActions.getInAppNotificationsUserFailure({
                                error: HttpResponseHelpers.parseErrorResponse(error)
                            })
                        )
                    )
                );
            })
        );
    });

    inAppNotificationSuccessToaster$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                InAppNotificationsActions.updateInAppNotificationSettingsSuccess,
                InAppNotificationsActions.markAllAsReadSuccess
            ),
            map((action) => showToasterMessage({ message: action.message }))
        );
    });

    inAppNotificationFailureToaster$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                InAppNotificationsActions.getInAppNotificationsFailure,
                InAppNotificationsActions.getUnreadInAppNotificationsCountFailure,
                InAppNotificationsActions.getInAppNotificationByIdFailure,
                InAppNotificationsActions.updateInAppNotificationSettingsFailure,
                InAppNotificationsActions.markAllAsReadFailure
            ),
            map((action) => showToasterMessage({ message: action.error }))
        );
    });
}
